import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  clearEmailVerificationSkip,
  saveEmailVerificationSkip,
} from '../utils/emailVerificationSkip';
import {
  clearPendingAccountUpdate,
  savePendingAccountUpdate,
  getPendingAccountUpdate,
} from '../utils/accountUpdateVerification';
import { normalizeEmail } from '../utils/normalizeEmail';

const API_URL = process.env.REACT_APP_API_URL ?? '';

const RESEND_INTERVAL = 60; // 60秒経たないとコード再送信をできないように
const VERIFICATION_CONTEXT_CHANGED_MESSAGE =
  '別のタブまたはウィンドウでログイン操作が行われました。この画面からは認証できません。ログインからやり直してください。';
const INVALID_VERIFICATION_CHALLENGE_MESSAGE =
  '認証セッションが無効です。ログインからやり直してください。';
const ACCOUNT_CHANGED_REAUTH_ERROR = 'account_changed_reauthentication_required';
const ACCOUNT_CHANGED_REAUTH_MESSAGE =
  '変更が完了しました。セキュリティ保護のため全端末からログアウトしました。変更後のメールアドレスとパスワードで再度ログインしてください。';

type VerificationLocationState = {
  email?: string;
  registration?: boolean;
  mode?: string;
  accountUpdate?: boolean;
  verificationChallengeId?: string;
};

const VerificationCode: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const navigationState = (location.state as VerificationLocationState) || {};
  const { setAuthFromTokenResponse, token, user } = useAuth();
  const [accountUpdateChallenge, setServerChallenge] = useState<{
    id: string;
    targetEmail: string;
    expiresAt: string;
  } | null>(null);
  const [isChallengeLoading, setIsChallengeLoading] = useState(Boolean(token));
  const pendingAccountUpdate = useMemo(() => getPendingAccountUpdate(), []);
  const emailFromParams = searchParams.get('email') || undefined;
  const modeFromParams = searchParams.get('mode');
  const normalizeMode = (
    value: string | null | undefined
  ): 'registration' | 'login' | 'emailChange' | 'accountUpdate' | undefined => {
    if (!value) {
      return undefined;
    }
    if (value === 'account-update') {
      return 'accountUpdate';
    }
    if (value === 'email-change') {
      return 'emailChange';
    }
    if (
      value === 'registration' ||
      value === 'login' ||
      value === 'emailChange' ||
      value === 'accountUpdate'
    ) {
      return value;
    }
    return undefined;
  };

  const normalizedStateMode = normalizeMode(navigationState.mode);
  const normalizedQueryMode = normalizeMode(modeFromParams);
  const standardVerificationRequested = Boolean(
    navigationState.registration || navigationState.verificationChallengeId ||
    normalizedStateMode === 'login' || normalizedStateMode === 'registration' ||
    normalizedQueryMode === 'login' || normalizedQueryMode === 'registration'
  );
  const serverChallenge = standardVerificationRequested ? null : accountUpdateChallenge;
  const accountUpdateParam = searchParams.get('accountUpdate');
  const accountUpdateRequested = !standardVerificationRequested && (
    navigationState.accountUpdate ||
    normalizedQueryMode === 'accountUpdate' ||
    accountUpdateParam === 'true' ||
    accountUpdateParam === '1' ||
    !!pendingAccountUpdate);
  const derivedMode =
    (serverChallenge ? 'accountUpdate' : undefined) ||
    (normalizedStateMode === 'accountUpdate' ? undefined : normalizedStateMode) ||
    (normalizedQueryMode === 'accountUpdate' ? undefined : normalizedQueryMode) ||
    (navigationState.registration ? 'registration' : 'login');
  const mode: 'registration' | 'login' | 'emailChange' | 'accountUpdate' =
    derivedMode;
  const email = normalizeEmail(
    serverChallenge?.targetEmail ||
      navigationState.email ||
      emailFromParams ||
      pendingAccountUpdate?.targetEmail ||
      ""
  );
  // Router history state belongs to the originating tab and survives reloads.
  const verificationChallengeId = typeof navigationState.verificationChallengeId === 'string'
    ? navigationState.verificationChallengeId
    : '';
  const [isVerificationContextInvalid, setIsVerificationContextInvalid] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_INTERVAL);
  const [skipForMonth, setSkipForMonth] = useState(false);
  const inFlightRef = useRef(false);
  const registration = navigationState.registration || mode === 'registration';
  const isEmailChange = mode === 'emailChange';
  const isAccountUpdate = Boolean(serverChallenge);
  const requiresAuthenticatedRequest = isEmailChange || isAccountUpdate;

  useEffect(() => {
    if (standardVerificationRequested || (!accountUpdateRequested && user?.mustVerifyCode)) {
      setIsChallengeLoading(false);
      return;
    }
    if (!token || !user?.id) {
      setIsChallengeLoading(false);
      if (pendingAccountUpdate) clearPendingAccountUpdate();
      return;
    }
    if (pendingAccountUpdate && pendingAccountUpdate.userId !== user.id) {
      clearPendingAccountUpdate();
    }
    let cancelled = false;
    setIsChallengeLoading(true);
    axios.get(`${API_URL}/api/auth/account-update/challenge`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(response => {
      if (cancelled) return;
      const challenge = response.data;
      setServerChallenge(challenge);
      savePendingAccountUpdate(user.id, challenge);
    }).catch(error => {
      if (cancelled) return;
      setServerChallenge(null);
      clearPendingAccountUpdate();
      if (accountUpdateRequested && error.response?.status === 404) {
        navigate('/user-settings', {
          replace: true,
          state: { accountUpdateMissing: true },
        });
      }
    }).finally(() => {
      if (!cancelled) setIsChallengeLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [accountUpdateRequested, navigate, pendingAccountUpdate, standardVerificationRequested, token, user?.id, user?.mustVerifyCode]);

  const verificationUnavailable = !requiresAuthenticatedRequest && !isChallengeLoading && (
    !verificationChallengeId || isVerificationContextInvalid
  );
  const displayedError = !requiresAuthenticatedRequest && !isChallengeLoading && !verificationChallengeId
    ? INVALID_VERIFICATION_CHALLENGE_MESSAGE
    : error;
  const returnToLogin = () => {
    // Shared cookies may belong to another tab; do not call the logout API.
    navigate('/login?reauth=verificationChanged', { replace: true });
  };

  // カウントダウン
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // 認証コード再送
  const handleResend = async () => {
    if (isLoading || isVerified || isChallengeLoading || verificationUnavailable || inFlightRef.current) {
      return;
    }
    setError("");
    setInfo("");

    if (requiresAuthenticatedRequest && !token) {
      setError('認証情報の有効期限が切れています。再度ログインしてください。');
      return;
    }

    inFlightRef.current = true;
    setIsLoading(true);
    try {
      // 再送APIの呼び出しはこの画面の操作に限定する．
      if (requiresAuthenticatedRequest) {
        const resendResponse = await axios.post(
          `${API_URL}/api/auth/resend-email-change`,
          { challengeId: serverChallenge?.id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setInfo('認証コードを再送信しました。メールをご確認ください。');
        const refreshed = resendResponse.data?.accountUpdateChallenge;
        if (isAccountUpdate && refreshed?.id && user?.id) {
          setServerChallenge(refreshed);
          savePendingAccountUpdate(user.id, refreshed);
        }
      } else {
        const resendResponse = await axios.post(`${API_URL}/api/auth/resend-verification`, {
          challengeId: verificationChallengeId,
        });
        const nextChallengeId = resendResponse.data?.verificationChallengeId;
        if (typeof nextChallengeId !== 'string' || !nextChallengeId) {
          setIsVerificationContextInvalid(true);
          setError(INVALID_VERIFICATION_CHALLENGE_MESSAGE);
          return;
        }
        setCode('');
        navigate(`${location.pathname}${location.search}`, {
          replace: true,
          state: { ...navigationState, verificationChallengeId: nextChallengeId },
        });
        setInfo("認証コードを再送信しました。メールをご確認ください。");
      }
      setResendCooldown(RESEND_INTERVAL);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (!requiresAuthenticatedRequest && (detail === 'verification_context_changed' || detail === 'invalid_verification_challenge')) {
        setIsVerificationContextInvalid(true);
        setError(detail === 'verification_context_changed'
          ? VERIFICATION_CONTEXT_CHANGED_MESSAGE : INVALID_VERIFICATION_CHALLENGE_MESSAGE);
      } else if (detail === 'verification_recently_sent') {
        setError('認証コードはすでに送信されています。しばらく経ってから再度お試しください。');
      } else if (err.response?.data?.detail === 'verification_expired') {
        setError('認証コードの有効期限が切れました。再度変更手続きを行ってください。');
      } else if (err.response?.data?.detail === 'no_account_update_pending') {
        setError('アカウント設定の変更リクエストが見つかりません。再度変更手続きを行ってください。');
      } else {
        setError("認証コードの送信に失敗しました。");
      }
    } finally {
      setIsLoading(false);
      inFlightRef.current = false;
    }
  };

  // 認証コード検証
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isVerified || isChallengeLoading || verificationUnavailable || inFlightRef.current) {
      return;
    }
    setError("");
    setInfo("");
    if (requiresAuthenticatedRequest && !token) {
      setError('認証情報の有効期限が切れています。再度ログインしてください。');
      return;
    }

    inFlightRef.current = true;
    setIsLoading(true);
    let shouldKeepLocked = false;
    try {
      let endpoint = `${API_URL}/api/auth/verify-email`;
      const payload: any = { code };
      let config: any = undefined;

      if (requiresAuthenticatedRequest) {
        endpoint = isAccountUpdate
          ? `${API_URL}/api/auth/verify-account-update`
          : `${API_URL}/api/auth/verify-email-change`;
        config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      } else {
        payload.challengeId = verificationChallengeId;
        if (!registration && skipForMonth) {
          payload.skipDurationDays = 30;
        }
      }

      const requestPayload = isAccountUpdate ? { code, challengeId: serverChallenge?.id } : requiresAuthenticatedRequest ? { code } : payload;
      const res = await axios.post(endpoint, requestPayload, config);
      if (isAccountUpdate && res.data?.error === ACCOUNT_CHANGED_REAUTH_ERROR) {
        clearPendingAccountUpdate();
        shouldKeepLocked = true;
        setIsVerified(true);
        setInfo(ACCOUNT_CHANGED_REAUTH_MESSAGE);
        window.setTimeout(() => {
          navigate('/login?reauth=passwordChanged', { replace: true });
        }, 2000);
        return;
      }

      const isSessionReady = await setAuthFromTokenResponse(res.data);
      if (!isSessionReady) {
        setError('セッション確認に失敗しました。再ログインが必要です。');
        return;
      }
      const verifiedUser = res.data.user;
      shouldKeepLocked = true;
      setIsVerified(true);
      setInfo('認証完了、遷移中です。');

      if (isEmailChange) {
        navigate('/user-settings', { state: { accountUpdateVerified: true } });
        return;
      }

      if (isAccountUpdate) {
        clearPendingAccountUpdate();
        navigate('/user-settings', { state: { accountUpdateVerified: true } });
        return;
      }

      // 1ヶ月間は認証スキップ
      if (!registration && skipForMonth) {
        const nextMonth = new Date();
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        saveEmailVerificationSkip(email, nextMonth, verifiedUser.id);
      } else {
        // 明示的にスキップしない場合は古い設定を削除
        clearEmailVerificationSkip(email);
      }

      if (
        verifiedUser.isAdmin ||
        verifiedUser.isVip ||
        verifiedUser.isTrial ||
        verifiedUser.hasActiveSubscription ||
        verifiedUser.isExpired
      ) {
        navigate("/", { replace: true });
      } else {
        navigate("/subscription", { replace: true });
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (!requiresAuthenticatedRequest && (detail === 'verification_context_changed' || detail === 'invalid_verification_challenge')) {
        setIsVerificationContextInvalid(true);
        setError(detail === 'verification_context_changed'
          ? VERIFICATION_CONTEXT_CHANGED_MESSAGE : INVALID_VERIFICATION_CHALLENGE_MESSAGE);
      } else if (detail === 'verification_expired') {
        setError('認証コードの有効期限が切れました。再度メールをご確認ください。');
      } else if (detail === 'invalid_verification_code') {
        setError('認証コードが正しくありません。');
      } else if (detail === 'verification_attempts_exceeded') {
        setError('認証コードの入力上限に達しました。コードを再送してやり直してください。');
      } else if (detail === 'verification_rate_limited') {
        setError('認証の試行回数が上限に達しました。しばらく時間をおいてください。');
      } else if (detail === 'invalid_verification_challenge') {
        setError('認証セッションが無効です。ログインからやり直してください。');
      } else if (detail === 'no_account_update_pending' || detail === 'no_email_change_requested') {
        if (isAccountUpdate) {
          clearPendingAccountUpdate();
        }
        setError('アカウント設定の変更リクエストが見つかりません。再度変更手続きを行ってください。');
      } else {
        setError('認証コードの確認に失敗しました。時間をおいて再度お試しください。');
      }
    } finally {
      if (!shouldKeepLocked) {
        setIsLoading(false);
        inFlightRef.current = false;
      }
    }
  };

  const isInteractionDisabled = isLoading || isVerified || isChallengeLoading || verificationUnavailable;

  if (!email) {
  if (isChallengeLoading && accountUpdateRequested) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>認証手続きを確認しています。</Typography>
      </Container>
    );
  }

    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Alert severity="error">{displayedError || 'メールアドレス情報がありません。ログインからやり直してください。'}</Alert>
          <Button fullWidth sx={{ mt: 2 }} onClick={returnToLogin}>ログイン画面へ戻る</Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          {isEmailChange ? 'メールアドレス変更の確認' : 'メール認証'}
        </Typography>
        <Typography align="center" sx={{ mb: 2 }}>
          {isAccountUpdate ? (
            <>
              アカウント設定で指定した内容（{email} 宛）に<br />
              認証コードを送信しました。<br />
              メールに記載されたコードを入力してください。
            </>
          ) : isEmailChange ? (
            <>
              アカウント設定で指定した新しいメールアドレス（{email}）宛に<br />
              認証コードを送信しました。<br />
              メールに記載されたコードを入力してください。
            </>
          ) : (
            <>
              ご登録のメールアドレス({email})宛に<br />
              6桁の認証コードを送信しました。<br />
              メールに記載されたコードを入力してください。
            </>
          )}
        </Typography>
        <Typography
          align="center"
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, whiteSpace: 'pre-line' }}
        >
          「nslab.co.jp」ドメインからのメールを受信できるように設定してください。
	  メールが届かない場合、迷惑メールフォルダの確認もお願いいたします。
        </Typography>

        {displayedError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {displayedError}
          </Alert>
        )}
        {info && (
          <Alert severity={isVerified ? 'success' : 'info'} sx={{ mb: 2 }}>
            {info}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            label="認証コード"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
            disabled={isInteractionDisabled}
          />
          {!registration && !isEmailChange && !isAccountUpdate && !user?.isViewer && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={skipForMonth}
                  onChange={(e) => setSkipForMonth(e.target.checked)}
                  color="primary"
                  disabled={isInteractionDisabled}
                />
              }
              label="この端末では1ヶ月間メール認証をスキップする"
              sx={{ mt: 1, mb: 2 }}
            />
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2, py: 1.5 }}
            disabled={isInteractionDisabled}
          >
            {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "認証する"}
          </Button>
          <Button
            fullWidth
            variant="text"
            sx={{ mt: 1 }}
            onClick={handleResend}
            disabled={isInteractionDisabled || resendCooldown > 0}
          >
            {resendCooldown > 0
              ? `再送信可能になるまで ${resendCooldown} 秒`
              : "認証コードを再送する"}
          </Button>
        </Box>
        {verificationUnavailable && (
          <Button fullWidth sx={{ mt: 2 }} onClick={returnToLogin}>
            ログイン画面へ戻る
          </Button>
        )}
      </Paper>
    </Container>
  );
};

export default VerificationCode;
